<template>
    <div class="bmi-plot">
        <div class="legend-container">
            <div class="legend-item male"> Male </div>
            <div class="legend-item female"> Female </div>
        </div>
        <svg ref="svg">
            <clipPath id="content-clip">
                <rect />
            </clipPath>
        </svg>
    </div>
</template>

<script>
import d3 from '@/utils/d3importer'
import widgetContent from '@/mixins/widgetContent'

export default {
    name: 'BmiPlot',
    mixins: [widgetContent],
    props: {
        selectedPopulation: String,
        trialConfigType: String
    },
    data() {
        return {
            xAxisLabel: null,
            averageBMIs: {
                "Underweight": { male: 19.1, female: 19.1 },
                "Healthy BMI": { male: 31.2, female: 31.2 },
                "Healthy BMI Scarred": { male: 31.2, female: 31.2 },
                "Healthy Adults": { male: 27.17, female: 28.3 },
                "Pediatric": { male: 19.8, female: 16.18 },
                "Dilated Cardiomyopathy": { male: 25.31, female: 19.82 },
                "Hypertrophic Cardiomyopathy": { male: 30.2, female: 25.8 },
                "Heart Failure": { male: 21.8, female: 22 },
                "Ischemia": { male: 21.8, female: 22 },
                "Myocardial Infarction": { male: 21.8, female: 22 },
            },
            errorBMIs: {
                "Underweight": { male: 0.391, female: 0.48 },
                "Healthy BMI": { male: 0.259, female: 0.259 },
                "Healthy BMI Scarred": { male: 0.259, female: 0.259 },
                "Healthy Adults": { male: 0.217, female: 0.243 },
                "Pediatric": { male: 0.391, female: 0.48 },
                "Dilated Cardiomyopathy": { male: 0.226, female: 0.246 },
                "Hypertrophic Cardiomyopathy": { male: 0.224, female: 0.224 },
                "Heart Failure": { male: 0.2, female: 0.227 },
                "Ischemia": { male: 0.2, female: 0.227 },
                "Myocardial Infarction": { male: 0.2, female: 0.227 },
            }
        }
    },
    mounted() {
        this.x = d3.scaleLinear()
        this.y = d3.scaleLinear()
        this.axisFuncs = {
            x: d3.axisBottom(this.x),
            y: d3.axisLeft(this.y)
        }
        this.updateChart()
    },
    watch: {
        selectedPopulation() {
            this.updateChart()
        }
    },
    computed: {
        contentSize() {
            return {
                width: Math.max(0, this.$el.getBoundingClientRect().width - this.margin.left - this.margin.right),
                height: Math.max(0, this.$el.getBoundingClientRect().height - this.margin.top - this.margin.bottom)
            }
        },
        margin() {
            let m = { left: 0, top: 0, bottom: 0, right: 0 }
            m.bottom = 80
            m.left = 70
            m.right = this.trialConfigType == 'single' ? 30 : 50

            if (this.forcedMargin) {
                Object.keys(this.forcedMargin).forEach((k) => m[k] = this.forcedMargin[k])
            }
            return m
        },
        maleBMICurve() {
            return [
                { bmi: 12, frequency: 0 }, { bmi: 14, frequency: 0 }, { bmi: 18.5, frequency: 10 }, 
                { bmi: 20, frequency: 23 }, { bmi: 22.5, frequency: 54 }, { bmi: 25, frequency: 75 }, { bmi: 26.9, frequency: 76 },
                { bmi: 27.5, frequency: 75 }, { bmi: 30, frequency: 57 }, { bmi: 32.5, frequency: 35 }, 
                { bmi: 35, frequency: 22 }, { bmi: 37.5, frequency: 10 }, { bmi: 40, frequency: 5 }, { bmi: 45, frequency: 3 }, { bmi: 50, frequency: 0 }
            ]
        },
        femaleBMICurve() {
            return [
                { bmi: 12, frequency: 0 }, { bmi: 14, frequency: 0 }, { bmi: 18.5, frequency: 16 }, 
                { bmi: 20, frequency: 30 }, { bmi: 22.5, frequency: 62.5 }, { bmi: 25, frequency: 72 },
                { bmi: 27.5, frequency: 58 }, { bmi: 30, frequency: 45 }, { bmi: 32.5, frequency: 30 }, 
                { bmi: 35, frequency: 23 }, { bmi: 37.5, frequency: 13 }, { bmi: 40, frequency: 8 }, { bmi: 45, frequency: 3 }, { bmi: 50, frequency: 0 }
            ]
        },
        selectedPopulationBMI() {
            return this.averageBMIs[this.selectedPopulation]
        },
        selectedPopulationBMIError() {
            return this.errorBMIs[this.selectedPopulation]
        }
    },
    methods: {
        updateChart() {
            this.x.range([0, this.contentSize.width]).domain([12, 50])
            let y = this.y.range([this.contentSize.height, 0]).domain([0, 100])
            y.domain([y.domain()[0] - 3, y.domain()[1] + 3])
            if (navigator.userAgent.toLowerCase().indexOf('firefox') > -1) {
                this.$refs.svg.createSVGPoint = null
            }
            let svg = d3.select(this.$refs.svg)
            let root = svg.selectAll('.root').data([null])
            root = root.merge(root.enter().append('g').attr('class', 'root'))
            this.updateAxes()
            let content = root.selectAll('.content').data([null])
            let contentEnter = content.enter().append('g').attr('class', 'content').attr('clip-path', 'url(#panel-content-clip)')
            contentEnter.append('rect').attr('class', 'background').on('mousemove', this.highlight).on('click', this.select)
            content = content.merge(contentEnter)
            content.attr('transform', `translate(${this.margin.left},${this.margin.top})`)
            content.select('.background')
                .attr('width', this.size.width - this.margin.left - this.margin.right)
                .attr('height', this.size.height - this.margin.top - this.margin.bottom)
            let clippath = d3.select(this.$refs.svg).select('#panel-content-clip')
            clippath.select('rect')
                .attr('width', this.size.width - this.margin.left - this.margin.right)
                .attr('height', this.size.height - this.margin.top - this.margin.bottom)
            this.content = content

            this.updateLines()
            this.drawPointsBySex()
        },
        updateAxes() {
            let root = d3.select(this.$refs.svg).select('.root')

            let xAxis = root.selectAll('.x-axis').data([null])
            let xAxisEnter = xAxis.enter().append('g').attr('class', 'axis x-axis')
            this.xAxisLabel = xAxisEnter.append('text').attr('class', 'label').text('BMI (kg/m2)')
            xAxis = xAxis.merge(xAxisEnter)
            xAxis.call(this.axisFuncs.x).attr('transform', `translate(${this.margin.left},${this.contentSize.height})`);
            xAxis.select('.label').attr("transform", `translate(${this.contentSize.width / 2},${this.margin.bottom - 40})`)

            let yAxis = root.selectAll('.y-axis').data([null])
            let yAxisEnter = yAxis.enter().append('g').attr('class', 'axis y-axis')
            yAxisEnter.append('text').attr('class', 'label').text('Frequency (% of adults)')
            yAxis = yAxis.merge(yAxisEnter)
            yAxis.call(this.axisFuncs.y).attr('transform', `translate(${this.margin.left},${this.margin.top})`);
            yAxis.select('.label').attr("transform", `translate(-${this.margin.left - 30},${this.contentSize.height / 2}) rotate(-90)`)

            let grid = root.selectAll('.grid').data([null])
            let gridEnter = grid.enter().append('g').attr('class', 'grid')
            grid = grid.merge(gridEnter)

            let xlines = grid.selectAll('.x-line').data(this.axisFuncs.x.scale().ticks(10))
            xlines.enter().append('line').attr('class', 'x-line')
            xlines.exit().remove()

            let ylines = grid.selectAll('.y-line').data(this.axisFuncs.y.scale().ticks(this.isEditing ? 8 : 6))
            ylines.enter().append('line').attr('class', 'y-line')
            ylines.exit().remove()

            grid.attr("transform", `translate(${this.margin.left},${this.margin.top})`)
            grid.selectAll('.y-line').attr('x2', this.contentSize.width).attr('y1', (d) => this.axisFuncs.y.scale()(d)).attr('y2', (d) => this.axisFuncs.y.scale()(d))
            grid.selectAll('.x-line').attr('y2', this.contentSize.height).attr('x1', (d) => this.axisFuncs.x.scale()(d)).attr('x2', (d) => this.axisFuncs.x.scale()(d))
        },
        updateLines() {
            // BMI RANGES
            const bmiRanges = [
                { range: "<18.5", label: "Underweight", color: "blue", start: 12, end: 18.5 },
                { range: "18.5-24.9", label: "Healthy weight", color: "green", start: 18.5, end: 25 },
                { range: "25-29.9", label: "Overweight", color: "yellow", start: 25, end: 30 },
                { range: "30-39.9", label: "Obese", color: "orange", start: 30, end: 40 },
                { range: "40+", label: "Severely obese", color: "red", start: 40, end: 50 }
            ];

            this.content.selectAll(".bmi-range")
                .data(bmiRanges)
                .enter()
                .append("rect")
                .attr("class", "bmi-range")
                .attr("x", d => this.x(d.start))
                .attr("width", d => this.x(d.end) - this.x(d.start))
                .attr("y", 0)
                .attr("height", this.contentSize.height)
                .attr("fill", d => d.color)
                .attr("fill-opacity", 0.1)

            this.content.selectAll(".bmi-label")
                .data(bmiRanges)
                .enter()
                .append("text")
                .attr("class", "bmi-label")
                .attr("x", d => this.x((d.start + d.end) / 2))
                .attr("y", 20)
                .attr("font-size", this.trialConfigType == 'single' ? 12 : 10)
                .attr("text-anchor", "middle")
                .text(d => `${d.label}`);

            // GENDER BMI LINES
            this.content.selectAll('.line').remove()
            let lineGenerator = d3.line()
                .x((d) => this.axisFuncs.x.scale()(d.bmi))
                .y((d) => this.axisFuncs.y.scale()(d.frequency))
                .curve(d3.curveBasis)

            let maleLine = this.content.append('path').attr('class', 'line').datum(this.maleBMICurve)
            maleLine.attr('d', lineGenerator)
                .attr('stroke-width', 1.5)
                .attr('stroke', '#007dc3')

            let femaleLine = this.content.append('path').attr('class', 'line').datum(this.femaleBMICurve)
            femaleLine.attr('d', lineGenerator)
                .attr('stroke-width', 1.5)
                .attr('stroke', '#E20DDB')
        },
        drawPointsBySex() {
            this.content.selectAll('.data-point').remove()
            this.content.selectAll('.error-bar').remove()
            this.content.selectAll('.error-cap').remove()

            const maleInterpolator = d3.scaleLinear()
                .domain(this.maleBMICurve.map(d => d.bmi))
                .range(this.maleBMICurve.map(d => d.frequency))
                .clamp(true);

            const femaleInterpolator = d3.scaleLinear()
                .domain(this.femaleBMICurve.map(d => d.bmi))
                .range(this.femaleBMICurve.map(d => d.frequency))
                .clamp(true);

            let interpolatedMaleFrequency = maleInterpolator(this.selectedPopulationBMI.male)
            this.content.append("circle")
                .attr("class", "data-point")
                .attr("cx", this.x(this.selectedPopulationBMI.male))
                .attr("cy", this.y(interpolatedMaleFrequency))
                .attr("r", 2.5);

            let interpolatedFemaleFrequency = femaleInterpolator(this.selectedPopulationBMI.female)
            this.content.append("circle")
                .attr("class", "data-point")
                .attr("cx", this.x(this.selectedPopulationBMI.female))
                .attr("cy", this.y(interpolatedFemaleFrequency))
                .attr("r", 2.5);

            // ERROR BARS
            this.content.append("line")
                .attr("class", "error-bar")
                .attr('stroke-width', 1)
                .attr('stroke', 'black')
                .attr("x1", this.x(this.selectedPopulationBMI.male - this.selectedPopulationBMIError.male))
                .attr("x2", this.x(this.selectedPopulationBMI.male + this.selectedPopulationBMIError.male))
                .attr("y1", this.y(interpolatedMaleFrequency))
                .attr("y2", this.y(interpolatedMaleFrequency));

            this.content.append("line")
                .attr("class", "error-cap")
                .attr('stroke', 'black')
                .attr('stroke-width', 1)
                .attr("x1", this.x(this.selectedPopulationBMI.male + this.selectedPopulationBMIError.male))
                .attr("x2", this.x(this.selectedPopulationBMI.male + this.selectedPopulationBMIError.male))
                .attr("y1", this.y(interpolatedMaleFrequency - 1))
                .attr("y2", this.y(interpolatedMaleFrequency + 1))

            this.content.append("line")
                .attr("class", "error-cap")
                .attr('stroke', 'black')
                .attr('stroke-width', 1)
                .attr("x1", this.x(this.selectedPopulationBMI.male - this.selectedPopulationBMIError.male))
                .attr("x2", this.x(this.selectedPopulationBMI.male - this.selectedPopulationBMIError.male))
                .attr("y1", this.y(interpolatedMaleFrequency - 1))
                .attr("y2", this.y(interpolatedMaleFrequency + 1))

            this.content.append("line")
                .attr("class", "error-bar")
                .attr('stroke-width', 1)
                .attr('stroke', 'black')
                .attr("x1", this.x(this.selectedPopulationBMI.female - this.selectedPopulationBMIError.female))
                .attr("x2", this.x(this.selectedPopulationBMI.female + this.selectedPopulationBMIError.female))
                .attr("y1", this.y(interpolatedFemaleFrequency))
                .attr("y2", this.y(interpolatedFemaleFrequency))

            this.content.append("line")
                .attr("class", "error-cap")
                .attr('stroke', 'black')
                .attr('stroke-width', 1)
                .attr("x1", this.x(this.selectedPopulationBMI.female - this.selectedPopulationBMIError.female))
                .attr("x2", this.x(this.selectedPopulationBMI.female - this.selectedPopulationBMIError.female))
                .attr("y1", this.y(interpolatedFemaleFrequency - 1))
                .attr("y2", this.y(interpolatedFemaleFrequency + 1))

            this.content.append("line")
                .attr("class", "error-cap")
                .attr('stroke', 'black')
                .attr('stroke-width', 1)
                .attr("x1", this.x(this.selectedPopulationBMI.female + this.selectedPopulationBMIError.female))
                .attr("x2", this.x(this.selectedPopulationBMI.female + this.selectedPopulationBMIError.female))
                .attr("y1", this.y(interpolatedFemaleFrequency - 1))
                .attr("y2", this.y(interpolatedFemaleFrequency + 1))


        },
    }
}
</script>

<style scoped lang="stylus">
.bmi-plot
    width: 100%
    height: 100%
    display: flex
    flex-direction: column
    background-color: white

    .legend-container
        display: flex;
        justify-content: center;
        margin-bottom: 10px;
        font-size: 12px

        .legend-item
            display: flex;
            padding-left: 5px
            padding-right: 5px

        .legend-item.male 
            text-decoration: underline
            text-decoration-color: #007dc3
            text-decoration-thickness: 2px
            text-underline-offset: 4px
            

        .legend-item.female 
            text-decoration: underline;
            text-decoration-color: #E20DDB
            text-decoration-thickness: 2px
            text-underline-offset: 4px
                

  svg
    height: 100%
    >>> .root
      .line
        // pointer-events none
        fill: none
      .grid
        line
          stroke-width 0.2px
          stroke #444
      .axis
        .tick
          line
            stroke-width 0.2px
          text
            fill $shuttle-gray
        .domain
          stroke-width 0px
        .label
          font-size 12px
          fill $shuttle-gray
          opacity 0.7
      .content
        .background
          fill: #fff
          opacity 0
    

</style>